/**
 * Resolve avatar URLs from profile API for display in <img> / MUI Avatar.
 * S3 URLs are proxied through the backend /media/ path when possible.
 */

import { getBackendBaseUrl as resolveBackendBaseUrl } from '@/setup/apiBaseUrl';

const getBackendBaseUrl = (): string => resolveBackendBaseUrl();

export type ProfileAvatarSource = {
  pic?: string | null;
  avatar?: string | null;
};

export const pickAvatarRawUrl = (source?: ProfileAvatarSource | null): string => {
  if (!source) {
    return '';
  }
  return String(source.avatar || source.pic || '').trim();
};

export const resolveAvatarDisplayUrl = (rawUrl?: string | null): string => {
  const trimmed = String(rawUrl ?? '').trim();
  if (!trimmed) {
    return '';
  }

  if (trimmed.startsWith('blob:') || trimmed.startsWith('data:')) {
    return trimmed;
  }

  const mediaMatch = trimmed.match(/(\/media\/avatars\/[^?#]+)/i);
  if (mediaMatch) {
    const backendBase = getBackendBaseUrl();
    if (backendBase) {
      return `${backendBase}${mediaMatch[1]}`;
    }
    return mediaMatch[1];
  }

  if (trimmed.startsWith('/media/avatars/')) {
    const backendBase = getBackendBaseUrl();
    return backendBase ? `${backendBase}${trimmed}` : trimmed;
  }

  return trimmed;
};

export const resolveProfileAvatarUrl = (source?: ProfileAvatarSource | null): string => {
  return resolveAvatarDisplayUrl(pickAvatarRawUrl(source));
};

/** Map profile API payload onto auth user shape (pic/avatar + name fields). */
export const normalizeProfileForAuth = <T extends Record<string, unknown>>(profile: T): T & {
  pic: string;
  avatar: string;
  firstname?: string;
  lastname?: string;
  fullname?: string;
} => {
  const source = profile as T & ProfileAvatarSource & {
    first_name?: string;
    last_name?: string;
    firstname?: string;
    lastname?: string;
    fullname?: string;
    full_name?: string;
  };

  const rawAvatar = pickAvatarRawUrl(source);
  const firstname = String(source.firstname || source.first_name || '').trim();
  const lastname = String(source.lastname || source.last_name || '').trim();
  const fullname =
    String(source.fullname || source.full_name || '').trim()
    || [firstname, lastname].filter(Boolean).join(' ');

  return {
    ...profile,
    pic: rawAvatar,
    avatar: rawAvatar,
    ...(firstname ? { firstname } : {}),
    ...(lastname ? { lastname } : {}),
    ...(fullname ? { fullname } : {}),
  };
};
